import { alpha, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, useTheme } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import { Edit, Ellipsis, Trash2 } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';

interface EntityPageMenuProps {
    menuItems?: (JSX.Element | undefined)[];
    onDelete?: () => void;
    openEditDialog?: () => void;
}
/**
 * Represents the EntityPageMenu component.
 *
 * @param {EntityPageMenuProps} onDelete - The function called when delete action is triggered.
 * @param {EntityPageMenuProps} openEditDialog - The function called when open-edit action is triggered.
 *
 * @returns {JSX.Element} The JSX element representing the EntityPageMenu component.
 */
export function EntityPageMenu({ menuItems, onDelete, openEditDialog }: EntityPageMenuProps) {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).entityPage;
    const offline = useSelector((state: AppState) => state.client.offline);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Return nothing if no menu items
    if (!onDelete && !openEditDialog && !menuItems?.length) return null;

    return (
        <>
            <IconButton
                aria-label="Weitere Aktionen"
                aria-controls={open ? 'entity-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                sx={{
                    color: theme.palette.text.secondary,
                    '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                }}
            >
                <Ellipsis size={20} />
            </IconButton>
            <Menu
                id="entity-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 160,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                            '& .MuiMenuItem-root': {
                                borderRadius: 1,
                                mx: 0.5,
                                my: 0.25,
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                },
                                '&.Mui-disabled': {
                                    opacity: 0.5,
                                },
                            },
                        },
                    },
                }}
            >
                {openEditDialog && (
                    <MenuItem
                        onClick={() => {
                            openEditDialog();
                            handleClose();
                        }}
                        disabled={offline}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <Edit size={16} />
                        </ListItemIcon>
                        <ListItemText primary={langStrings.edit} />
                    </MenuItem>
                )}
                {onDelete && (
                    <MenuItem
                        onClick={() => {
                            onDelete();
                            handleClose();
                        }}
                        disabled={offline}
                        sx={{
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                                '& .MuiListItemIcon-root, & .MuiListItemText-root': {
                                    color: theme.palette.error.main,
                                },
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <Trash2 size={16} />
                        </ListItemIcon>
                        <ListItemText primary={langStrings.delete} />
                    </MenuItem>
                )}
                {menuItems?.map(
                    (menuItem, index) =>
                        menuItem &&
                        React.cloneElement(menuItem, {
                            key: index,
                            close: handleClose,
                        }),
                )}
            </Menu>
        </>
    );
}
